import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export default function PrintLedger() {
  const [searchParams] = useSearchParams();
  const partyName = searchParams.get("party") || "";
  
  const [data, setData] = useState({ transactions: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!partyName) {
      setLoading(false);
      return;
    }
    
    api.getAccountingReports().then(res => {
      setData(res);
    }).catch(console.error).finally(() => {
      setLoading(false);
    });
  }, [partyName]);

  useEffect(() => {
    if (!loading && partyName) {
      setTimeout(() => {
        window.print();
      }, 500);
    }
  }, [loading, partyName]);

  if (loading) return <div className="p-20 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>;
  if (!partyName) return <div className="p-20 text-center">No party specified</div>;

  const partyTxs = data.transactions.filter(tx => 
    (tx.party || tx.customer_name || "Unknown") === partyName
  ).sort((a, b) => new Date(a.date) - new Date(b.date)); // Sort ascending for statement

  let runningBalance = 0;
  
  const formatCurrency = (val) => {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(val || 0);
  };

  const totalIn = partyTxs.filter(t => t.type === "IN").reduce((acc, curr) => acc + (parseFloat(curr.amount) || 0), 0);
  const totalOut = partyTxs.filter(t => t.type === "OUT").reduce((acc, curr) => acc + (parseFloat(curr.amount) || 0), 0);
  const finalBalance = totalIn - totalOut;

  return (
    <div className="bg-white min-h-screen text-slate-900 p-8 max-w-4xl mx-auto print:p-0 font-sans">
      <div className="mb-8 border-b-2 border-slate-200 pb-6 text-center">
        <h1 className="text-3xl font-black text-slate-900 mb-1">Happy Journey 4 U</h1>
        <p className="text-slate-500 text-sm mb-4">Official Ledger Statement</p>
        
        <div className="flex justify-between items-end text-left mt-8">
          <div>
            <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-1">Statement For</p>
            <h2 className="text-xl font-bold text-slate-800">{partyName}</h2>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-1">Date Generated</p>
            <p className="text-sm font-medium">{new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
          <p className="text-xs text-slate-500 font-medium mb-1">Total Received (IN)</p>
          <p className="text-lg font-bold text-green-600">{formatCurrency(totalIn)}</p>
        </div>
        <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
          <p className="text-xs text-slate-500 font-medium mb-1">Total Paid (OUT)</p>
          <p className="text-lg font-bold text-red-600">{formatCurrency(totalOut)}</p>
        </div>
        <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
          <p className="text-xs text-slate-500 font-medium mb-1">Net Balance</p>
          <p className={`text-lg font-bold ${finalBalance >= 0 ? "text-slate-900" : "text-red-600"}`}>
            {formatCurrency(Math.abs(finalBalance))} {finalBalance >= 0 ? "(Cr)" : "(Dr)"}
          </p>
        </div>
      </div>

      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b-2 border-slate-800">
            <th className="py-3 px-2 text-left font-bold text-slate-800">Date</th>
            <th className="py-3 px-2 text-left font-bold text-slate-800">Reference / Notes</th>
            <th className="py-3 px-2 text-right font-bold text-slate-800">Debit (OUT)</th>
            <th className="py-3 px-2 text-right font-bold text-slate-800">Credit (IN)</th>
            <th className="py-3 px-2 text-right font-bold text-slate-800">Balance</th>
          </tr>
        </thead>
        <tbody>
          {partyTxs.length === 0 ? (
            <tr><td colSpan="5" className="py-8 text-center text-slate-500">No transactions recorded.</td></tr>
          ) : (
            partyTxs.map((tx, i) => {
              const amt = parseFloat(tx.amount) || 0;
              if (tx.type === "IN") runningBalance += amt;
              else runningBalance -= amt;
              
              return (
                <tr key={i} className="border-b border-slate-200">
                  <td className="py-3 px-2">{tx.date}</td>
                  <td className="py-3 px-2">
                    <div className="font-medium">{tx.reference || "—"}</div>
                    {tx.notes && <div className="text-xs text-slate-500 mt-0.5">{tx.notes}</div>}
                    {tx.bankAccount && <div className="text-xs text-slate-500">Bank: {tx.bankAccount}</div>}
                  </td>
                  <td className="py-3 px-2 text-right text-red-600">{tx.type === "OUT" ? formatCurrency(amt) : ""}</td>
                  <td className="py-3 px-2 text-right text-green-600">{tx.type === "IN" ? formatCurrency(amt) : ""}</td>
                  <td className={`py-3 px-2 text-right font-bold ${runningBalance >= 0 ? "text-slate-800" : "text-red-600"}`}>
                    {formatCurrency(Math.abs(runningBalance))} {runningBalance >= 0 ? "Cr" : "Dr"}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      
      <div className="mt-16 text-center text-sm text-slate-500 print:fixed print:bottom-8 print:w-full">
        <p>This is a computer-generated statement.</p>
      </div>
    </div>
  );
}
